import React, { useState, useEffect, Fragment } from "react";
import { Link, withRouter, RouteComponentProps } from "react-router-dom";
import { UtilityFunctions } from "../../utils/UtilityFunctions";
import { Helmet } from "react-helmet";
import "./InterviewSetup.css";

interface InterviewSetupProps extends RouteComponentProps<any> {}

const InterviewSetup: React.FC<InterviewSetupProps> = (props) => {
  const [candidateName, setCandidateName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [employerName, setEmployerName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [btnClick, setBtnClick] = useState(false);

  useEffect(() => {
    try {
      const userData = localStorage.getItem("user_data");
      if (userData) {
        const parsed = JSON.parse(userData);
        const firstName = parsed?.firstName || parsed?.user?.firstName || "";
        const lastName = parsed?.lastName || parsed?.user?.lastName || "";
        const fullName = [firstName, lastName].filter(Boolean).join(" ");
        if (fullName) setCandidateName(fullName);
      }
    } catch (e) {
      // ignore parse errors
    }
  }, []);

  const isCandidateValid = candidateName.trim().length > 0;
  const isJobTitleValid = jobTitle.trim().length > 0;
  const isEmployerValid = employerName.trim().length > 0;
  const isFormValid = isCandidateValid && isJobTitleValid && isEmployerValid;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setBtnClick(true);
    if (!isFormValid) return;

    const params = new URLSearchParams();
    params.set("candidateName", candidateName.trim());
    params.set("jobTitle", jobTitle.trim());
    params.set("employerName", employerName.trim());
    if (jobDescription.trim()) {
      params.set("jobDescription", jobDescription.trim());
    }

    window.location.href = `https://simulation.mploi.com/?${params.toString()}`;
  };

  return (
    <Fragment>
      <Helmet>
        <title>Configure Your Interview - MPLOI AI Interview Simulator</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Urbanist:wght@400;500;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <div className="is-setup">
        {/* Navigation */}
        <nav className="is-setup-nav">
          <img
            src={UtilityFunctions.getImageURL() + "logo_White.svg"}
            alt="MPLOI Logo"
            className="is-setup-nav-logo"
            onClick={() => props.history.push("/")}
          />
          <ul className="is-setup-nav-links">
            <li><a href="https://mploi.com/#about">About Us</a></li>
            <li><a href="https://mploi.com/#services">Services</a></li>
            <li><a href="https://mploi.com/blog">Blog</a></li>
            <li><a href="https://mploi.com/#pricing">Pricing</a></li>
            <li><a href="https://mploi.com/#contact">Contact</a></li>
          </ul>
          <div className="is-setup-nav-actions">
            <button
              className="is-setup-btn-back"
              onClick={() => props.history.goBack()}
            >
              ← Back
            </button>
          </div>
        </nav>

        {/* Hero */}
        <section className="is-setup-hero">
          <h1>Configure Your<br />Interview</h1>
          <p>
            Tell us about the role you're preparing for. We'll tailor the AI
            interview experience to match the job and company.
          </p>
        </section>

        {/* Form */}
        <section className="is-setup-form-section">
          <div className="is-setup-form-container">
            <h2 className="is-setup-form-title">Interview Details</h2>
            <p className="is-setup-form-subtitle">
              Fill in the details below to personalise your interview simulation
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="is-setup-form-group">
                <label className="is-setup-form-label">
                  Your Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. John Smith"
                  className={`is-setup-form-input ${btnClick && !isCandidateValid ? "error" : ""}`}
                  value={candidateName}
                  onChange={(e) => { setCandidateName(e.target.value); setBtnClick(false); }}
                />
                {btnClick && !isCandidateValid && (
                  <div className="is-setup-form-error">Please enter your name</div>
                )}
              </div>

              <div className="is-setup-form-group">
                <label className="is-setup-form-label">
                  Job Title <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Senior Software Engineer"
                  className={`is-setup-form-input ${btnClick && !isJobTitleValid ? "error" : ""}`}
                  value={jobTitle}
                  onChange={(e) => { setJobTitle(e.target.value); setBtnClick(false); }}
                />
                {btnClick && !isJobTitleValid && (
                  <div className="is-setup-form-error">Please enter the job title</div>
                )}
              </div>

              <div className="is-setup-form-group">
                <label className="is-setup-form-label">
                  Company / Employer <span className="required">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Google, ANZ Bank, Local Cafe"
                  className={`is-setup-form-input ${btnClick && !isEmployerValid ? "error" : ""}`}
                  value={employerName}
                  onChange={(e) => { setEmployerName(e.target.value); setBtnClick(false); }}
                />
                {btnClick && !isEmployerValid && (
                  <div className="is-setup-form-error">Please enter the company name</div>
                )}
              </div>

              <div className="is-setup-form-group">
                <label className="is-setup-form-label">
                  Job Description <span style={{ opacity: 0.5, textTransform: 'lowercase', fontWeight: 400 }}>(optional)</span>
                </label>
                <textarea
                  placeholder="Paste the job description here for a more tailored interview experience..."
                  className="is-setup-form-textarea"
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                />
                <div className="is-setup-form-hint">
                  Adding a job description helps the AI ask more relevant questions
                </div>
              </div>

              <button
                type="submit"
                className="is-setup-submit-btn"
                disabled={btnClick && !isFormValid}
              >
                START INTERVIEW →
              </button>
            </form>
          </div>
        </section>

        {/* Footer */}
        <footer className="is-setup-footer">
          <p>Questions? Contact our support team</p>
          <p><a href="mailto:info@mploi.com">info@mploi.com</a></p>
          <p style={{ marginTop: "16px" }}>
            <Link to="/privacy">Privacy Policy</Link>{" | "}
            <Link to="/terms">Terms & Conditions</Link>
          </p>
          <p style={{ marginTop: "24px", fontSize: "12px" }}>
            All rights reserved. Copyright © {new Date().getFullYear()} MPLOI
          </p>
        </footer>
      </div>
    </Fragment>
  );
};

export default withRouter(InterviewSetup);
